import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, AlertTriangle, Sparkles, RefreshCw, Send, Users,
  HardHat, MessageSquare, CalendarClock, TrendingUp, TrendingDown, Minus, UserPlus,
} from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import SyncBadge from "@/components/SyncBadge";
import api from "@/lib/api";
import { AGENT_VISUALS } from "@/lib/agents";

const RISK_COLORS = {
  high: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/30", label: "Risque élevé" },
  medium: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30", label: "À surveiller" },
  low: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30", label: "OK" },
};

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [pilotes, setPilotes] = useState([]);
  const [convs, setConvs] = useState([]);
  const [briefings, setBriefings] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sync, setSync] = useState(null);

  const refresh = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [k, c, b, a] = await Promise.all([
        api.get("/saas/kpis"),
        api.get("/conversations").catch(() => ({ data: [] })),
        api.get("/briefings").catch(() => ({ data: [] })),
        api.get("/agents").catch(() => ({ data: { active: [] } })),
      ]);
      setKpis(k.data.kpis);
      setPilotes(k.data.pilotes || []);
      setSync(k.data.sync || { sync_at: k.data.fetched_at, stale: false });
      setConvs(c.data || []);
      setBriefings(b.data || []);
      setTeam(a.data.active || []);
      if (k.data.sync?.stale) {
        toast.warning(k.data.sync.error || "CHANTIX est temporairement indisponible");
      }
    } catch (e) {
      toast.error("Impossible de charger le cockpit");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    refresh(true);
    const t = setInterval(() => refresh(true), 60_000); // auto-refresh 1 min
    return () => clearInterval(t);
  }, []);

  const atRisk = pilotes.filter((p) => p.health?.risk === "high");
  const places = kpis ? (kpis.places_remaining ?? 0) : 0;

  return (
    <ConsoleShell
      title="Cockpit GABRYX"
      breadcrumb={["GABRYX", "Cockpit"]}
      actions={
        <div className="flex items-center gap-2">
          <SyncBadge sync={sync} onRefresh={() => refresh()} loading={refreshing} />
          <button
            onClick={() => refresh()}
            disabled={refreshing}
            data-testid="cockpit-refresh-btn"
            className="hidden sm:flex text-xs border border-white/15 px-3 py-1.5 hover:bg-white/5 transition items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
      }
    >
      <div className="px-5 lg:px-8 py-6 space-y-6 max-w-[1600px]">
        {/* KPI strip — 8 cells dense */}
        <section data-testid="kpi-strip" className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-px bg-white/10 border border-white/10">
          <Kpi label="Pilotes" value={kpis ? `${kpis.pilots_total}/${kpis.pilots_quota}` : "—"} sub={kpis ? `${kpis.pilots_active} actifs` : ""} testid="kpi-pilotes" highlight={places > 0 && places <= 3 ? "warn" : null} />
          <Kpi label="Places rest." value={kpis ? kpis.places_remaining : "—"} sub="phase pilote" testid="kpi-places" />
          <Kpi label="Utilisateurs" value={kpis ? kpis.users_total : "—"} sub={kpis ? `${kpis.users_paying} payants` : ""} testid="kpi-users" />
          <Kpi label="Chantiers" value={kpis ? kpis.chantiers_total : "—"} sub="live CHANTIX" testid="kpi-chantiers" />
          <Kpi label="Activation" value={kpis ? `${kpis.activation_rate}%` : "—"} sub="taux pilotes actifs" testid="kpi-activation" />
          <Kpi label="À risque" value={atRisk.length} sub="pilotes" testid="kpi-risk" highlight={atRisk.length > 0 ? "warn" : null} />
          <Kpi label="Feedback" value={kpis ? kpis.feedback_total : "—"} sub={kpis ? `${kpis.feedback_bugs} bugs · ${kpis.feedback_suggestions} idées` : ""} testid="kpi-feedback" />
          <Kpi label="Briefings" value={briefings.length} sub="générés" testid="kpi-briefings" />
        </section>

        {/* Team strip — 10 agents with photos, always visible */}
        <section className="border border-white/10 bg-[#0f0f0f]" data-testid="team-strip">
          <header className="flex items-center justify-between px-5 py-3 border-b border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Mon équipe</div>
              <h2 className="font-display text-base font-semibold mt-0.5">{team.length} agents en service</h2>
            </div>
            <Link to="/app/agents" className="text-xs text-zinc-400 hover:text-white flex items-center gap-1" data-testid="team-view-all">
              Voir détails <ArrowRight className="w-3 h-3" />
            </Link>
          </header>
          <div className="grid grid-cols-5 lg:grid-cols-10 gap-px bg-white/5">
            {team.map((a) => {
              const v = AGENT_VISUALS[a.code];
              return (
                <Link
                  key={a.code}
                  to={`/app/chat/${a.code}`}
                  data-testid={`team-agent-${a.code}`}
                  className="group bg-[#0f0f0f] hover:bg-white/5 p-3 flex flex-col items-center text-center transition"
                  title={`${a.name} — ${a.role}`}
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden mb-2" style={{ boxShadow: `0 0 0 1.5px ${a.color}` }}>
                    {v?.avatar && <img src={v.avatar} alt={a.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="font-display text-[12px] font-semibold leading-tight">{a.name}</div>
                  <div className="text-[9px] text-zinc-500 truncate w-full mt-0.5">{a.role}</div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Main grid: pilotes (2 cols) + sidebar quick actions (1 col) */}
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Pilotes en risque */}
          <section className="lg:col-span-2 border border-white/10 bg-[#0f0f0f]" data-testid="pilotes-risk-panel">
            <header className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Pilotes en risque</div>
                <h2 className="font-display text-base font-semibold mt-0.5">{atRisk.length} pilote{atRisk.length > 1 ? "s" : ""} à réactiver</h2>
              </div>
              <Link to="/app/pilotes" data-testid="pilotes-view-all" className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
                Voir tous <ArrowRight className="w-3 h-3" />
              </Link>
            </header>
            {loading ? (
              <div className="p-12 text-center text-zinc-500 text-sm">Chargement…</div>
            ) : atRisk.length === 0 ? (
              <div className="p-12 text-center">
                <Sparkles className="w-6 h-6 mx-auto mb-3 text-emerald-400" />
                <div className="font-display text-base mb-1">Tous les pilotes sont en bonne santé</div>
                <p className="text-xs text-zinc-500">Aucune action prioritaire pour l&apos;instant.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-white/5">
                    <th className="text-left px-5 py-2.5 font-normal">Pilote</th>
                    <th className="text-left px-3 py-2.5 font-normal">Email</th>
                    <th className="text-right px-3 py-2.5 font-normal">Chantiers</th>
                    <th className="text-right px-3 py-2.5 font-normal">Score</th>
                    <th className="text-right px-5 py-2.5 font-normal">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {atRisk.slice(0, 6).map((p) => (
                    <PiloteRow key={p.user_id || p.id || p.email} p={p} compact />
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Quick actions */}
          <aside className="border border-white/10 bg-[#0f0f0f] p-5" data-testid="quick-actions">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Actions rapides</div>
            <div className="space-y-2">
              <QuickAction
                to="/app/chat/gaby"
                icon={MessageSquare}
                title="Briefer GABY"
                sub="Demande une synthèse stratégique"
                color="#3B82F6"
                testid="qa-gaby"
              />
              <QuickAction
                to="/app/chat/clara"
                icon={Sparkles}
                title="CLARA marketing"
                sub="Rédige un post LinkedIn"
                color="#EC4899"
                testid="qa-clara"
              />
              <QuickAction
                to="/app/chat/visio"
                icon={TrendingUp}
                title="Snapshot VISIO"
                sub="État SaaS du portefeuille"
                color="#F97316"
                testid="qa-visio"
              />
              <QuickAction
                to="/app/briefings"
                icon={CalendarClock}
                title="Générer briefing"
                sub="Maintenant, par tous les agents"
                color="#10B981"
                testid="qa-briefing"
              />
              <QuickAction
                to="/app/prospects"
                icon={UserPlus}
                title="Acquisition prospects"
                sub="Ajouter + CLARA outreach"
                color="#0A66C2"
                testid="qa-prospects"
              />
              <QuickAction
                to="/app/pilotes"
                icon={Users}
                title="Vue Pilotes complète"
                sub={`${pilotes.length} pilotes · ${atRisk.length} à risque`}
                color="#A855F7"
                testid="qa-pilotes"
              />
            </div>
          </aside>
        </div>

        {/* Bottom row: conversations + last briefing */}
        <div className="grid lg:grid-cols-2 gap-4">
          <section className="border border-white/10 bg-[#0f0f0f]" data-testid="recent-conversations">
            <header className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Conversations</div>
                <h2 className="font-display text-base font-semibold mt-0.5">Activité récente</h2>
              </div>
              <Link to="/app/chat" className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
                Voir tout <ArrowRight className="w-3 h-3" />
              </Link>
            </header>
            {convs.length === 0 ? (
              <div className="p-8 text-center text-xs text-zinc-500">Aucune conversation pour l&apos;instant.</div>
            ) : (
              <ul className="divide-y divide-white/5">
                {convs.slice(0, 6).map((c) => {
                  const v = AGENT_VISUALS[c.agent_code];
                  return (
                    <li key={c.id}>
                      <Link to={`/app/chat/${c.agent_code}/${c.id}`} data-testid={`recent-conv-${c.id}`} className="flex items-center gap-3 px-5 py-2.5 hover:bg-white/5 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: v?.color || "#fff" }} />
                        <span className="font-mono text-[10px] text-zinc-500 w-12 flex-shrink-0">{c.agent_code.toUpperCase()}</span>
                        <span className="flex-1 truncate">{c.title}</span>
                        <span className="text-[10px] text-zinc-500 font-mono flex-shrink-0">{new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="border border-white/10 bg-[#0f0f0f]" data-testid="last-briefing">
            <header className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Dernier briefing</div>
                <h2 className="font-display text-base font-semibold mt-0.5">{briefings[0]?.date_label || "Aucun briefing"}</h2>
              </div>
              <Link to="/app/briefings" className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
                Historique <ArrowRight className="w-3 h-3" />
              </Link>
            </header>
            {briefings[0] ? (
              <div className="p-5">
                <div className="text-xs text-zinc-500 font-mono mb-3">
                  {briefings[0].sections?.length || 0} sections · livraison {briefings[0].delivery?.email || "—"}
                </div>
                <div className="space-y-1.5">
                  {(briefings[0].sections || []).slice(0, 5).map((s) => (
                    <div key={s.code} className="flex items-center gap-2 text-xs">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                      <span className="font-display font-medium w-16">{s.name}</span>
                      <span className="text-zinc-500 truncate">{(s.content || "").split("\n")[0].slice(0, 70)}…</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-xs text-zinc-500">Aucun briefing — <Link to="/app/briefings" className="underline">en générer un</Link></div>
            )}
          </section>
        </div>
      </div>
    </ConsoleShell>
  );
}

function Kpi({ label, value, sub, testid, highlight }) {
  const cls = highlight === "warn"
    ? "bg-amber-500/5 border-l-2 border-amber-400"
    : "bg-[#0f0f0f]";
  return (
    <div className={`${cls} p-3.5`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 leading-none">{label}</div>
      <div className="font-display text-[24px] font-semibold tracking-tighter mt-1.5 leading-none">{value}</div>
      <div className="text-[10px] text-zinc-500 mt-1.5 font-mono truncate">{sub || "\u00A0"}</div>
    </div>
  );
}

function PiloteRow({ p, compact }) {
  const risk = p.health?.risk || "low";
  const c = RISK_COLORS[risk];
  return (
    <tr className="border-b border-white/5 hover:bg-white/5 transition" data-testid={`pilote-row-${p.user_id || p.id || p.email}`}>
      <td className="px-5 py-2.5">
        <div className="font-medium text-sm">{p.full_name || p.name || p.email || "—"}</div>
      </td>
      <td className="px-3 py-2.5 text-xs text-zinc-400 font-mono truncate max-w-[180px]">{p.email || "—"}</td>
      <td className="px-3 py-2.5 text-right text-sm">{p.health?.chantiers_count ?? 0}</td>
      <td className="px-3 py-2.5 text-right">
        <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.1em] px-2 py-0.5 ${c.bg} ${c.text}`}>
          {p.health?.score ?? 0}
        </span>
      </td>
      <td className="px-5 py-2.5 text-right">
        <Link
          to={`/app/chat/clara`}
          state={{ prefill: `Rédige-moi un email de réengagement pour le pilote ${p.full_name || p.email}, en risque de churn (score ${p.health?.score}/100, ${p.health?.chantiers_count} chantiers, ${p.health?.has_feedback ? "a laissé un feedback" : "sans feedback"}).` }}
          className="text-[11px] text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
        >
          Réengager <Send className="w-3 h-3" />
        </Link>
      </td>
    </tr>
  );
}

function QuickAction({ to, icon: Icon, title, sub, color, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex items-center gap-3 p-2.5 hover:bg-white/5 transition border border-transparent hover:border-white/10"
    >
      <div className="w-8 h-8 grid place-items-center flex-shrink-0" style={{ background: color + "15", color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium leading-tight">{title}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{sub}</div>
      </div>
      <ArrowUpRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-white transition" />
    </Link>
  );
}
